'use client';
import { useEffect, useRef, useState } from 'react';
import { LANGUAGES, Language, usePreferences } from '@/lib/preferences';
import { useT } from '@/lib/i18n';

const FLAGS: Record<Language, string> = {
  en: '🇬🇧', tr: '🇹🇷', fr: '🇫🇷', de: '🇩🇪',
  es: '🇪🇸', it: '🇮🇹', zh: '🇨🇳', ja: '🇯🇵',
  ko: '🇰🇷', hi: '🇮🇳', ru: '🇷🇺', ar: '🇸🇦', pt: '🇵🇹',
};

export function LanguagePicker() {
  const { prefs, setPrefs } = usePreferences();
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 px-2 py-1 rounded-lg text-sm text-slate-600 hover:bg-sky-200 hover:text-slate-900 transition-colors select-none"
        aria-label="Change language"
      >
        <span className="text-base leading-none">{FLAGS[prefs.language]}</span>
        <span className="font-medium uppercase tracking-wide text-xs">{prefs.language}</span>
        <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-44 rounded-xl border border-sky-200 bg-white shadow-lg z-50 py-1 overflow-hidden">
          {LANGUAGES.map(lang => (
            <button
              key={lang}
              onClick={() => { setPrefs({ language: lang }); setOpen(false); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors
                ${prefs.language === lang
                  ? 'bg-violet-50 text-violet-700 font-medium'
                  : 'text-slate-700 hover:bg-sky-50'}`}
            >
              <span className="text-base leading-none">{FLAGS[lang]}</span>
              <span>{t(`lang.${lang}`)}</span>
              {prefs.language === lang && (
                <svg className="ml-auto w-3.5 h-3.5 text-violet-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
